import { useEffect, useState } from 'react';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getSelectionById, getSelections } from 'src/shared/services/selectionService';
import { useTranslation } from 'react-i18next';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from '../models/memberSearchFormState';

type SelectionTemplatePickerProps = {
  updateSelectionOptions: boolean;
  updateState: (state: any) => void;
  setUpdateSelectionOptions: (updateOptions: boolean) => void;
  setCurrentSelection: (updateSavedSelection: MemberSearchFormState | null) => void;
  resetAll: () => void;
};

export const SelectionTemplatePicker = (props: SelectionTemplatePickerProps) => {
  const { updateState, setUpdateSelectionOptions, updateSelectionOptions, setCurrentSelection, resetAll } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const [selection, setSelection] = useState<any | undefined>();
  const [options, setOptions] = useState<Selection[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchSelections = async () => {
      try {
        const response = await getSelections(true, 0, 500, 'selection_id', 'desc');
        if (isMounted()) {
          setOptions(response.data.results);
          setUpdateSelectionOptions(false);
        }
      } catch (error) {
        if (isMounted()) ConsoleHelper.error('Error fetching selections:', error);
      }
    };
    fetchSelections();
  }, [isMounted, setUpdateSelectionOptions, updateSelectionOptions]);

  useEffect(() => {
    if (selection) {
      setLoading(true);
      const fetchSelection = async () => {
        try {
          const res = await getSelectionById(selection.selection_id);
          if (isMounted()) {
            const pageingState = getDefaultMemberSearchFormState();
            const selectionState = res.data.json as MemberSearchFormState;
            if (selectionState && selectionState.status && selectionState.status.length > 0) delete pageingState.status; // Remove default status if selection has status
            if (selectionState && selectionState.sortCol) delete pageingState.sortCol; // Remove default sortCol if selection has sortCol
            if (selectionState && selectionState.sortDir) delete pageingState.sortDir; // Remove default sortCol if selection has sortCol
            if (selectionState && selectionState.pageSize) delete pageingState.pageSize; // Remove default pageSize if selection has pageSize
            if (selectionState && selectionState.pageIndex) selectionState.pageIndex = 0; //  if selection has pageIndex, set to zero
            const resetState = {
              ...res.data.json,
              ...pageingState,
              selectionId: selection.selection_id,
              selectionName: selection.selectionName,
              selectionInfo: selection.selectionInfo,
            };
            setCurrentSelection(resetState as MemberSearchFormState);
            updateState(resetState);
            setSelection(undefined);
          }
        } catch (e) {
          if (isMounted()) ConsoleHelper.error(e);
        } finally {
          if (isMounted()) setLoading(false);
        }
      };
      fetchSelection();
    }
  }, [isMounted, selection, setCurrentSelection, updateState]);

  return (
    <>
      {loading && <LoadingOverlay loading />}
      <AutocompleteDropdown
        keyProp='selection_id'
        options={options}
        nameProp='selectionName'
        label={t('member:memberAdvancedSearch.selectSelection')}
        placeholder={t('member:memberAdvancedSearch.noSelection')}
        emptyLabel={t('member:memberAdvancedSearch.noSelection')}
        callback={(x) => {
          setSelection(x);
          if (x === undefined || x === null) {
            setCurrentSelection(null);
            resetAll();
          }
        }}
      />
    </>
  );
};
